/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injectable } from '@angular/core';
import { Observable, take, throwError } from 'rxjs';
import { AlertService } from '@kanzi-apes/kanzi-alerts';
import { KEYS, MESSAGES_ERROR } from './messages-errores';
import { TranslateService, _ } from '@ngx-translate/core';

/**
 * @author Hugo Andrés Escobar Ciceri
 *
 * Service to control the errors messages.
 */
@Injectable({
  providedIn: 'root',
})
export class KanziControlErrorsService {
  translateService = inject(TranslateService);
  alertMessage = inject(AlertService);

  /**
   *
   * @param operation
   * @returns {Observable<T>} Observable with the error message.
   * @description Function to control errors.
   */
  public handleError<T>(operation: any): (error: any) => Observable<T> {
    return (error: any): Observable<T> => {
      let errorText = '';
      if (error.error) {
        if (error.status >= 500) {
          errorText = operation;
        } else if (error.status >= 400 && error.status < 500) {
          const errors = error.error;
          if (typeof errors === 'string') {
            // The backend body is a plain string message.
            errorText = errors;
          } else if (errors?.message) {
            errorText = this.setMessage(errors);
          } else {
            const keys = Object.keys(errors);
            errorText = '';
            for (let i = 0; i < keys.length; i++) {
              // DRF field errors arrive as string[] (e.g. { field: ["msg"] }),
              // but business errors arrive as a plain string (e.g.
              // { detail: "..." }). Reading [0] on a string would return just
              // its first character, so normalize the value first.
              const rawValue = errors[keys[i]];
              const fieldMessage = Array.isArray(rawValue)
                ? rawValue.join(' ')
                : `${rawValue}`;
              this.translateService
                .get(_(fieldMessage))
                .pipe(take(1))
                .forEach((value) => {
                  if (i > 0) {
                    errorText = errorText + '\n';
                  }
                  errorText = errorText + `${value}`;
                });
            }
          }
        }
      }
      if (errorText === '') {
        errorText = operation;
      }

      // Let the app keep running by returning an empty result.
      return throwError(() => errorText);
    };
  }

  /**
   *
   * @param message {string}
   *
   * Function to open an alert with error message.
   */
  private log(message: string) {
    this.alertMessage.error(message);
  }

  /**
   *
   * @param message {string}
   *
   * Function to open an alert with error message.
   */
  private setMessage(response: any) {
    console.log('🚀 RESPONSE', response);

    let nonFieldErrorMessage = '';
    if (response.details?.non_field_errors?.length > 0) {
      nonFieldErrorMessage = response.details.non_field_errors[0];
    }

    const message = response.message;
    const keys = Object.keys(response);
    let detail = '';
    keys
      .filter((i) => i !== 'message')
      .forEach((key) => {
        detail =
          detail +
          ' ' +
          `${
            KEYS[key as keyof typeof KEYS]
              ? KEYS[key as keyof typeof KEYS]
              : key
          }: ${response[key]}`;
      });

    return `${
      MESSAGES_ERROR[message as keyof typeof MESSAGES_ERROR]
        ? MESSAGES_ERROR[message as keyof typeof MESSAGES_ERROR]
        : message
    } ${nonFieldErrorMessage ? ` - ${nonFieldErrorMessage}` : ''} ${detail}`;
  }
}
